// Scan History — the radiology counterpart of Lab History.
//
// Lists every saved scan report in a date range, one row per patient per
// scan date. Expanding a row shows the individual scans done that day; each
// can be viewed on screen or reprinted through the same ScanPrintDialog the
// Requested Scan page uses, so history prints look identical to same-day
// prints (branded/standard template, narrative layout, impression).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Stack,
  Collapse,
  Tooltip,
  MenuItem,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  DateRange as DateRangeIcon,
  GetApp as FetchIcon,
  Visibility as ViewIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Print as PrintIcon,
  LibraryBooks as CombinedIcon,
  History as HistoryIcon,
  Scanner as ScannerIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { loadScanHistory, loadScanHistoryReport, loadPharmacyInfo } from '../BaseURL';
import ScanPrintDialog from './ScanPrintDialog';
import { ErrorMessage } from '../Message';

const ACCENT = '#1976d2';

// Local YYYY-MM-DD — toISOString() would shift the day for users east/west of UTC.
const ymd = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
};
const prettyDate = (s) => {
  if (!s) return '-';
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y) return s;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const QUICK_RANGES = [
  { key: 'today', label: 'Today',   from: () => daysAgo(0) },
  { key: '7d',    label: '7 days',  from: () => daysAgo(6) },
  { key: '30d',   label: '30 days', from: () => daysAgo(29) },
  { key: '90d',   label: '90 days', from: () => daysAgo(89) },
];

const currentUserName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('userInfo') || '{}');
    // Same precedence RequestedScan uses when it stamps scan_result.user.
    return u?.user_name || u?.name || '';
  } catch (_) { return ''; }
};

const patientFromRow = (row, referringDoctor = '') => ({
  id: row.id,
  fullName: row.name,
  age: row.age,
  gender: row.gender,
  tel: row.tel,
  location: row.location,
  referring_doctor: referringDoctor,
});

// ── Expandable visit row ────────────────────────────────────────────────────
const ScanHistoryRow = ({ row, onView, onPrint }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentTint = alpha(ACCENT, isDark ? 0.18 : 0.10);
  const accentTintHover = alpha(ACCENT, isDark ? 0.28 : 0.18);
  const successTint = alpha(theme.palette.success.main, isDark ? 0.18 : 0.12);
  const successTintHover = alpha(theme.palette.success.main, isDark ? 0.30 : 0.22);
  const subtleBg = isDark ? alpha(theme.palette.common.white, 0.04) : alpha(theme.palette.common.black, 0.02);

  const actionSx = (bg, bgHover) => ({
    bgcolor: bg,
    transition: 'all 0.2s ease',
    '&:hover': { bgcolor: bgHover, transform: 'translateY(-1px)', boxShadow: 1 },
  });

  const multi = row.scans.length > 1;

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > *': { borderBottom: 'unset' },
          ...(open && { bgcolor: accentTint, '&:hover': { bgcolor: accentTint } }),
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              transition: 'transform 0.25s ease, background-color 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              bgcolor: open ? ACCENT : 'transparent',
              color: open ? 'white' : 'inherit',
              '&:hover': { bgcolor: open ? '#1565c0' : 'action.hover' },
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>{row.id || '-'}</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.name || '-'}</TableCell>
        <TableCell>
          <Chip label={row.gender || '-'} size="small" variant="outlined" sx={{ minWidth: 60, fontWeight: 500 }} />
        </TableCell>
        <TableCell>{row.age ?? '-'}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{prettyDate(row.dat)}</TableCell>
        <TableCell sx={{ maxWidth: 280 }}>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {row.scans.slice(0, 2).map((s) => (
              <Chip
                key={s.scan}
                label={s.scan}
                size="small"
                sx={{ fontWeight: 600, bgcolor: accentTint, color: ACCENT, maxWidth: 200 }}
              />
            ))}
            {row.scans.length > 2 && (
              <Chip label={`+${row.scans.length - 2}`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
            )}
          </Stack>
        </TableCell>
        <TableCell>{row.performed_by.join(', ') || '-'}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <Tooltip title={multi ? 'View all scans for this date' : 'View report'}>
              <IconButton color="success" size="small" onClick={() => onView(row)} sx={actionSx(successTint, successTintHover)}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={multi ? `Print all ${row.scans.length} scans as one report` : 'Print report'}>
              <IconButton color="primary" size="small" onClick={() => onPrint(row)} sx={actionSx(accentTint, accentTintHover)}>
                {multi ? <CombinedIcon fontSize="small" /> : <PrintIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 2, p: 2.5, borderRadius: 2,
                bgcolor: alpha(ACCENT, isDark ? 0.06 : 0.04),
                border: '1px solid', borderColor: alpha(ACCENT, isDark ? 0.25 : 0.18),
                borderLeft: '4px solid', borderLeftColor: ACCENT,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <ScannerIcon sx={{ color: ACCENT }} />
                <Typography variant="subtitle1" sx={{ color: ACCENT, fontWeight: 600 }}>
                  Scans for {row.name} on {prettyDate(row.dat)}
                </Typography>
                <Chip
                  label={`${row.scans.length} scan${row.scans.length === 1 ? '' : 's'}`}
                  size="small" variant="outlined"
                  sx={{ color: ACCENT, borderColor: ACCENT }}
                />
              </Stack>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
              >
                <Table size="small" aria-label="scans on this date">
                  <TableHead>
                    <TableRow>
                      {['Scan', 'Report Type', 'Sections Reported', 'Performed By', 'Actions'].map((h) => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.scans.map((s) => (
                      <TableRow key={s.scan} hover sx={{ '&:nth-of-type(even)': { bgcolor: subtleBg } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{s.scan}</TableCell>
                        <TableCell>
                          <Chip
                            label={s.format === 'narrative' ? 'Narrative' : 'Parameter'}
                            size="small"
                            color={s.format === 'narrative' ? 'secondary' : 'default'}
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>{s.findings}</TableCell>
                        <TableCell>{s.performed_by.join(', ') || '-'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title={`View ${s.scan}`}>
                              <IconButton color="success" size="small" onClick={() => onView(row, s.scan)} sx={actionSx(successTint, successTintHover)}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={`Print only ${s.scan}`}>
                              <IconButton color="primary" size="small" onClick={() => onPrint(row, s.scan)} sx={actionSx(accentTint, accentTintHover)}>
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────
const ScanHistory = () => {
  const me = useMemo(currentUserName, []);

  const [fromDate, setFromDate] = useState(daysAgo(6));
  const [toDate, setToDate] = useState(daysAgo(0));
  const [searchTerm, setSearchTerm] = useState('');
  const [staffFilter, setStaffFilter] = useState('all');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [companyInfo, setCompanyInfo] = useState(null);

  // View dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewCtx, setViewCtx] = useState(null); // { row, scan, report }

  // Print dialog
  const [printOpen, setPrintOpen] = useState(false);
  const [printReportData, setPrintReportData] = useState({});
  const [printScanFormats, setPrintScanFormats] = useState({});
  const [printPatient, setPrintPatient] = useState(null);

  const userInfo = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('userInfo')) || {}; } catch (_) { return {}; }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(loadPharmacyInfo);
        const json = await res.json();
        if (json.success && json.results) setCompanyInfo(json.results);
      } catch (e) {
        console.error('Error loading company info:', e);
      }
    })();
  }, []);

  const fetchHistory = async (from = fromDate, to = toDate) => {
    if (!from || !to) {
      ErrorMessage('Error', 'Please select both from date and to date');
      return;
    }
    if (from > to) {
      ErrorMessage('Error', 'From date cannot be later than to date');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(loadScanHistory, { params: { fromDate: from, toDate: to } });
      setData(res.data?.success && Array.isArray(res.data.results) ? res.data.results : []);
      setPage(0);
    } catch (e) {
      console.error('Error fetching scan history:', e);
      setData([]);
      ErrorMessage('Error', 'Could not load scan history');
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  // Open on the last 7 days so the page is useful without any clicks.
  useEffect(() => { fetchHistory(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const applyQuickRange = (r) => {
    const from = r.from();
    const to = daysAgo(0);
    setFromDate(from);
    setToDate(to);
    fetchHistory(from, to);
  };
  const activeQuick = QUICK_RANGES.find((r) => r.from() === fromDate && toDate === daysAgo(0))?.key;

  const staffOptions = useMemo(() => {
    const set = new Set();
    data.forEach((r) => r.performed_by.forEach((u) => set.add(u)));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [data]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return data.filter((r) => {
      if (staffFilter !== 'all' && !r.performed_by.includes(staffFilter)) return false;
      if (!q) return true;
      return (
        String(r.id || '').toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q) ||
        r.scans.some((s) => s.scan.toLowerCase().includes(q))
      );
    });
  }, [data, searchTerm, staffFilter]);

  useEffect(() => { setPage(0); }, [searchTerm, staffFilter]);

  const stats = useMemo(() => ({
    visits: filtered.length,
    scans: filtered.reduce((n, r) => n + r.scans.length, 0),
    patients: new Set(filtered.map((r) => r.id)).size,
  }), [filtered]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const loadReport = async (row, scan) => {
    const params = { patientId: row.id, date: row.dat };
    if (scan) params.scan = scan;
    const res = await axios.get(loadScanHistoryReport, { params });
    if (!res.data?.success || !res.data.totalRecords) return null;
    return res.data;
  };

  const handleView = async (row, scan = null) => {
    setViewCtx({ row, scan, report: null });
    setViewOpen(true);
    setViewLoading(true);
    try {
      const report = await loadReport(row, scan);
      setViewCtx({ row, scan, report });
    } catch (e) {
      console.error('Error loading scan report:', e);
      setViewCtx({ row, scan, report: null });
    } finally {
      setViewLoading(false);
    }
  };

  const openPrint = (row, report) => {
    setPrintReportData(report.reportData);
    setPrintScanFormats(report.scanFormats || {});
    setPrintPatient(patientFromRow(row, report.referringDoctor));
    setPrintOpen(true);
  };

  const handlePrint = async (row, scan = null) => {
    try {
      const report = await loadReport(row, scan);
      if (!report) {
        ErrorMessage('No results', 'No saved findings were found for this scan.');
        return;
      }
      openPrint(row, report);
    } catch (e) {
      console.error('Error loading scan report for print:', e);
      ErrorMessage('Error', 'Could not load the scan report');
    }
  };

  const headCell = { color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 };
  const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } };

  // Flattened sections for the view dialog: [{ scan, format, rows, impression }]
  const viewScans = useMemo(() => {
    const report = viewCtx?.report;
    if (!report) return [];
    const byScan = Object.values(report.reportData || {})[0] || {};
    return Object.keys(byScan).sort().map((scan) => {
      const rows = byScan[scan];
      return {
        scan,
        format: report.scanFormats?.[scan] || 'parameter',
        rows,
        impression: (rows.find((r) => r.impression && r.impression.trim()) || {}).impression || '',
        performedBy: [...new Set(rows.map((r) => r.user).filter(Boolean))].join(', '),
      };
    });
  }, [viewCtx]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header — same treatment as the Scan Dashboard */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: ACCENT, letterSpacing: '1.4px', textTransform: 'uppercase', mb: 0.5 }}>
          Radiology Records
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(25,118,210,0.32)',
          }}>
            <HistoryIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.7rem' }, fontWeight: 700, color: 'text.primary', letterSpacing: '-0.4px', lineHeight: 1.1 }}>
              Scan History
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.25 }}>
              Look up, view and reprint any saved scan report
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ mb: 3, px: 2, py: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
          {QUICK_RANGES.map((r) => (
            <Chip
              key={r.key}
              label={r.label}
              size="small"
              onClick={() => applyQuickRange(r)}
              color={activeQuick === r.key ? 'primary' : 'default'}
              variant={activeQuick === r.key ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Stack>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small" label="From" type="date"
              value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small" label="To" type="date"
              value={toDate} onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth size="small"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by patient name, ID or scan…"
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}><CloseIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={8} sm={4} md={2.5}>
            <TextField
              select fullWidth size="small" label="Performed by"
              value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
              sx={fieldSx}
            >
              <MenuItem value="all">All staff</MenuItem>
              {me && staffOptions.includes(me) && <MenuItem value={me}>Me ({me})</MenuItem>}
              {staffOptions.filter((u) => u !== me).map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={4} sm={2} md={1.5}>
            <Button
              fullWidth variant="contained" onClick={() => fetchHistory()} disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FetchIcon fontSize="small" />}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: 2 } }}
            >
              {loading ? 'Fetching…' : 'Fetch'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Scan Records
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip size="small" label={`${stats.patients} patient${stats.patients === 1 ? '' : 's'}`} variant="outlined" />
            <Chip size="small" label={`${stats.visits} visit${stats.visits === 1 ? '' : 's'}`} variant="outlined" />
            <Chip size="small" label={`${stats.scans} scan${stats.scans === 1 ? '' : 's'}`} color="primary" variant="outlined" />
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Alert severity="info">
            {!fetched
              ? 'Select a date range and click "Fetch" to load scan history.'
              : data.length === 0
                ? 'No saved scan reports in the selected date range.'
                : 'No records match your search or staff filter.'}
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow sx={{ background: `linear-gradient(90deg, #1565c0 0%, ${ACCENT} 100%)` }}>
                    <TableCell sx={{ ...headCell, width: 56 }} />
                    <TableCell sx={headCell}>Patient ID</TableCell>
                    <TableCell sx={headCell}>Patient Name</TableCell>
                    <TableCell sx={headCell}>Gender</TableCell>
                    <TableCell sx={headCell}>Age</TableCell>
                    <TableCell sx={headCell}>Scan Date</TableCell>
                    <TableCell sx={headCell}>Scans</TableCell>
                    <TableCell sx={headCell}>Performed By</TableCell>
                    <TableCell sx={headCell}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((row) => (
                    <ScanHistoryRow
                      key={`${row.id}|${row.dat}`}
                      row={row}
                      onView={handleView}
                      onPrint={handlePrint}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filtered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </Paper>

      {/* View report */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: ACCENT, color: 'white', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScannerIcon />
          <Box sx={{ flexGrow: 1 }}>
            {viewCtx?.scan || 'Scan Report'}
            <Typography component="div" sx={{ fontSize: '0.8rem', opacity: 0.9 }}>
              {viewCtx?.row?.name} · ID {viewCtx?.row?.id} · {prettyDate(viewCtx?.row?.dat)}
            </Typography>
          </Box>
          <IconButton onClick={() => setViewOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : viewScans.length === 0 ? (
            <Alert severity="info">No saved findings were found for this scan.</Alert>
          ) : (
            <Stack spacing={3}>
              {viewScans.map((s) => (
                <Box key={s.scan}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ color: ACCENT, fontWeight: 600, flexGrow: 1 }}>{s.scan}</Typography>
                    {s.performedBy && (
                      <Typography variant="body2" color="text.secondary">Performed by {s.performedBy}</Typography>
                    )}
                  </Stack>
                  <TableContainer component={Paper} variant="outlined">
                    {s.format === 'narrative' ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: '30%' }}>Section</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Findings</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {s.rows.map((r, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>{r.para}</TableCell>
                              <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{r.finding}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Finding</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Range</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Flag</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {s.rows.map((r, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ fontWeight: 600 }}>{r.para}</TableCell>
                              <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{r.finding}</TableCell>
                              <TableCell>{r.unit || '-'}</TableCell>
                              <TableCell>{r.ref_range || '-'}</TableCell>
                              <TableCell>
                                {r.flag && r.flag !== '-' ? (
                                  <Chip label={r.flag} size="small" color={String(r.flag).toLowerCase() === 'normal' ? 'success' : 'warning'} />
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TableContainer>
                  {s.impression && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Impression:</Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{s.impression}</Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            disabled={viewLoading || !viewCtx?.report}
            onClick={() => { setViewOpen(false); openPrint(viewCtx.row, viewCtx.report); }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      <ScanPrintDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        reportData={printReportData}
        selectedPatient={printPatient}
        userInfo={userInfo}
        companyInfo={companyInfo}
        scanFormats={printScanFormats}
      />
    </Box>
  );
};

export default ScanHistory;
